// frontend/src/Context/AuthContext.jsx
import React, { useState, useEffect, useContext, createContext } from "react";
import API from "../helpers/API";
import jwt_decode from "jwt-decode"; // Corrected import

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({ user: null, token: "" });
  const [isInitialized, setIsInitialized] = useState(false);

  // Client-side token expiration check
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const decoded = jwt_decode(token);
      const expirationTime = decoded.exp * 1000;
      const currentTime = Date.now();
      return expirationTime < currentTime;
    } catch (error) {
      console.error("Token decoding error:", error);
      return true;
    }
  };

  const updateAPIHeaders = (token) => {
    if (token) {
      API.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete API.defaults.headers.common["Authorization"];
    }
  };

  const setAuthWithHeaders = (authData) => {
    setAuth(authData);
    updateAPIHeaders(authData?.token);

    if (authData?.token && authData?.user) {
      localStorage.setItem("userAuthDetails", JSON.stringify(authData));
    } else {
      localStorage.removeItem("userAuthDetails");
    }
  };

  const clearAuth = () => {
    setAuth({ user: null, token: "" });
    updateAPIHeaders(null);
    localStorage.removeItem("userAuthDetails");
  };

  // Server-side token validation
  const validateToken = async (token) => {
    if (!token) return false;

    try {
      const response = await API.get("/auth/validate-token", {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data?.valid === true;
    } catch (error) {
      console.log("Token validation failed:", error.message);
      return false;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const data = localStorage.getItem("userAuthDetails");

      if (data) {
        try {
          const parseData = JSON.parse(data);
          const token = parseData?.token;

          // Only set auth if token is not expired
          if (token && !isTokenExpired(token)) {
            setAuth({ user: parseData.user, token });
            updateAPIHeaders(token);
          } else {
            localStorage.removeItem("userAuthDetails");
          }
        } catch (error) {
          console.error("Initialization error:", error);
          localStorage.removeItem("userAuthDetails");
        }
      }
      setIsInitialized(true);
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (isInitialized) {
      updateAPIHeaders(auth?.token);
    }
  }, [auth?.token, isInitialized]);

  const contextValue = {
    auth,
    setAuth: setAuthWithHeaders,
    clearAuth,
    isInitialized,
    isTokenExpired
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  const authArray = [context.auth, context.setAuth];
  authArray.auth = context.auth;
  authArray.setAuth = context.setAuth;
  authArray.clearAuth = context.clearAuth;
  authArray.isInitialized = context.isInitialized;
  authArray.isTokenExpired = context.isTokenExpired;
  authArray.isAuthenticated = !!(context.auth?.token && context.auth?.user);
  authArray.isAdmin = context.auth?.user?.role === 1;
  authArray.isRecruiter = context.auth?.user?.role === 2;
  authArray.isSuperAdmin = context.auth?.user?.role === 3;
  authArray.user = context.auth?.user;
  authArray.token = context.auth?.token;

  return authArray;
};

export { useAuth, AuthProvider };
